"use client";

import {
  Container,
  Title,
  Text,
  Stack,
  SimpleGrid,
  Group,
  Box,
  Anchor,
  Card,
} from "@mantine/core";
import Link from "next/link";
import { Season, Subseason } from "@/constants";
import { SeasonMetadata } from "@/app/[locale]/color-palette/colorPaletteConstants";
import QuizPhotoUpload from "@/components/QuizPhotoUpload";
import { seasonHubData } from "./hubData";

const formatName = (text: string): string => {
  return text
    .split(/[-_]/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
};

// reused verbatim for title/description/keywords/introduction only).

interface ColorPaletteSeasonContentProps {
  season: string;
  seasonData: Season;
  metadata: SeasonMetadata;
}

const subSeasonSlug = (key: string): string => key.toLowerCase().replace(/_/g, "-");

export default function ColorPaletteSeasonContent({
  season,
  seasonData,
  metadata,
}: ColorPaletteSeasonContentProps) {
  const seasonKey = season.toUpperCase();
  const seasonDisplayName = formatName(season);
  const hubCopy = seasonHubData[seasonKey];
  const subSeasonKeys = Object.keys(seasonData.subseasons);
  const otherSeasons = ["spring", "summer", "autumn", "winter"].filter(
    (s) => s !== season.toLowerCase()
  );

  // Sample a handful of colors from each subseason for the hero swatch strip.
  const heroSwatches = subSeasonKeys.flatMap((key) =>
    seasonData.subseasons[key].primaryColors.slice(0, 6)
  );

  return (
    <>
      {/* ============================================ */}
      {/* HERO */}
      {/* ============================================ */}
      <Box
        style={{
          background: `linear-gradient(135deg, ${heroSwatches[0]?.hex || "#f5f5f5"}15, ${heroSwatches[heroSwatches.length - 1]?.hex || "#e0e0e0"}15)`,
          paddingTop: 60,
          paddingBottom: 80,
          marginBottom: 60,
        }}
      >
        <Container size="lg">
          <Box ta="center" mb={40}>
            <Text size="xs" tt="uppercase" c="dimmed" mb={16} style={{ letterSpacing: "0.15em" }}>
              Seasonal Color Analysis
            </Text>
            <Title
              order={1}
              style={{
                fontSize: "clamp(2.5rem, 6vw, 4rem)",
                fontWeight: 300,
                letterSpacing: "-0.02em",
                lineHeight: 1.1,
                marginBottom: 16,
              }}
            >
              {seasonDisplayName} Color Palette
            </Title>
            <Text
              size="lg"
              c="dimmed"
              style={{ lineHeight: 1.7, maxWidth: 640, margin: "0 auto" }}
            >
              {metadata.introduction}
            </Text>
          </Box>

          <SimpleGrid cols={{ base: 6, sm: 9, md: 12 }} spacing={8}>
            {heroSwatches.map((color, i) => (
              <Box
                key={`${color.key}-${i}`}
                title={color.name}
                style={{
                  aspectRatio: "1",
                  backgroundColor: color.hex,
                  borderRadius: 8,
                }}
              />
            ))}
          </SimpleGrid>
        </Container>
      </Box>

      <Container size="md" pb={100}>
        {/* Breadcrumb */}
        <Anchor component={Link} href="/color-palette" c="dimmed" size="sm" mb={40} display="block">
          ← All Palettes
        </Anchor>

        {/* ============================================ */}
        {/* THE THREE SUBSEASONS */}
        {/* ============================================ */}
        <Box mb={80} id="subseasons">
          <Text
            size="xs"
            tt="uppercase"
            c="dimmed"
            mb={32}
            style={{ letterSpacing: "0.15em", fontWeight: 500 }}
          >
            The Three {seasonDisplayName} Subseasons
          </Text>
          <SimpleGrid cols={{ base: 1, sm: 3 }} spacing={24}>
            {subSeasonKeys.map((key) => {
              const subseason = seasonData.subseasons[key];
              const copy = hubCopy?.subseasons[key];
              const slug = subSeasonSlug(key);
              return (
                <Card key={key} withBorder padding="lg" radius="md" component={Link} href={`/color-palette/${season.toLowerCase()}/${slug}`}>
                  <SimpleGrid cols={4} spacing={4} mb={16}>
                    {subseason.primaryColors.slice(0, 8).map((color) => (
                      <Box
                        key={color.key}
                        title={color.name}
                        style={{
                          aspectRatio: "1",
                          backgroundColor: color.hex,
                          borderRadius: 4,
                        }}
                      />
                    ))}
                  </SimpleGrid>
                  <Text fw={600} size="md" mb={8}>
                    {copy?.displayName || formatName(key)}
                  </Text>
                  <Text size="sm" c="dimmed" style={{ lineHeight: 1.6 }}>
                    {copy?.summary}
                  </Text>
                </Card>
              );
            })}
          </SimpleGrid>
        </Box>

        {/* ============================================ */}
        {/* COMPARISON TABLE */}
        {/* ============================================ */}
        {hubCopy && (
          <Box mb={80} id="compare">
            <Text
              size="xs"
              tt="uppercase"
              c="dimmed"
              mb={32}
              style={{ letterSpacing: "0.15em", fontWeight: 500 }}
            >
              Which {seasonDisplayName} Are You?
            </Text>
            <Box style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: "left", padding: "10px 12px", borderBottom: "2px solid #eee", fontSize: 13, color: "#666" }}>
                      &nbsp;
                    </th>
                    {subSeasonKeys.map((key) => (
                      <th
                        key={key}
                        style={{ textAlign: "left", padding: "10px 12px", borderBottom: "2px solid #eee", fontSize: 13, fontWeight: 600 }}
                      >
                        {hubCopy.subseasons[key]?.displayName || formatName(key)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(["undertone", "depth", "chroma", "bestIf"] as const).map((rowKey) => (
                    <tr key={rowKey}>
                      <td style={{ padding: "10px 12px", borderBottom: "1px solid #f0f0f0", fontSize: 13, fontWeight: 500, color: "#666" }}>
                        {rowKey === "bestIf" ? "Best if..." : formatName(rowKey)}
                      </td>
                      {subSeasonKeys.map((key) => (
                        <td key={key} style={{ padding: "10px 12px", borderBottom: "1px solid #f0f0f0", fontSize: 13, lineHeight: 1.5 }}>
                          {hubCopy.subseasons[key]?.[rowKey]}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </Box>
          </Box>
        )}

        {/* ============================================ */}
        {/* UPLOAD CTA */}
        {/* ============================================ */}
        <Box mb={80} id="upload">
          <QuizPhotoUpload
            quizName={`${seasonDisplayName} Palette Hub`}
            headline={`Not sure which ${seasonDisplayName} you are? Upload a photo — free.`}
            subtext="AI draping compares your photo against all three subseasons and tells you which one actually fits, plus 50+ personalized shades."
          />
        </Box>

        {/* ============================================ */}
        {/* FAQ */}
        {/* ============================================ */}
        {hubCopy?.faqs && hubCopy.faqs.length > 0 && (
          <Box mb={80} id="faq">
            <Text
              size="xs"
              tt="uppercase"
              c="dimmed"
              mb={40}
              style={{ letterSpacing: "0.15em", fontWeight: 500 }}
            >
              Common Questions
            </Text>
            <Stack gap={32}>
              {hubCopy.faqs.map((faq, i) => (
                <Box key={i}>
                  <Text fw={500} mb={12} size="md">
                    {faq.question}
                  </Text>
                  <Text size="md" c="dark.6" style={{ lineHeight: 1.8 }}>
                    {faq.answer}
                  </Text>
                </Box>
              ))}
            </Stack>
          </Box>
        )}

        {/* ============================================ */}
        {/* FOOTER LINKS */}
        {/* ============================================ */}
        <Box>
          <Text
            size="xs"
            tt="uppercase"
            c="dimmed"
            mb={16}
            style={{ letterSpacing: "0.15em", fontWeight: 500 }}
          >
            More Seasons
          </Text>
          <Group gap={16} wrap="wrap">
            {otherSeasons.map((s) => (
              <Anchor key={s} component={Link} href={`/color-palette/${s}`} size="sm">
                {formatName(s)} Palette
              </Anchor>
            ))}
            <Anchor component={Link} href="/color-palette" size="sm" c="dimmed">
              All Palettes
            </Anchor>
          </Group>
        </Box>
      </Container>
    </>
  );
}

// Exported so page.tsx can build the FAQSchema without duplicating copy.
